<script setup>
/* global axios */
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import OmnupAiAtamaPenceresi from './OmnupAiAtamaPenceresi.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  // Konusmayi AI asistana ver / geri al. Durum alanina dokunmuyor.
  items.push(
    aiAtandi.value
      ? {
          icon: 'i-lucide-user-round',
          label: t('CONVERSATION.OMNUP_AI.KALDIR'),
          action: 'omnup_ai_kaldir',
          value: 'omnup_ai_kaldir',
        }
      : {
          icon: 'i-lucide-sparkles',
          label: t('CONVERSATION.OMNUP_AI.ATA'),
          action: 'omnup_ai_ata',
          value: 'omnup_ai_ata',
        }
  );

  // Atanmis konusmada talimati sonradan duzenleyebilmek gerekiyor;
  // aksi halde talimati degistirmek icin once atamayi kaldirip
  // yeniden atmak gerekirdi.
  if (aiAtandi.value) {
    items.push({
      icon: 'i-lucide-pencil-line',
      label: t('CONVERSATION.OMNUP_AI.TALIMAT_DUZENLE'),
      action: 'omnup_ai_talimat',
      value: 'omnup_ai_talimat',
    });
  }

  return items;
});

const hesapKimligi = computed(() => store.getters.getCurrentAccountId);

// Konusma AI asistana atanmis mi? Isaret konusmanin ozniteliklerinde.
// Kutuya bagli asistan konusmayi DURUM uzerinden (pending) de yurutur;
// bu gorulmeyince menude "ata" cikiyor, temsilci devralamiyordu.
const aiAtandi = computed(
  () =>
    currentChat.value?.custom_attributes?.omnup_ai === true ||
    currentChat.value?.status === 'pending'
);

// Bu konusmaya ozel talimat.
const aiTalimat = computed(
  () => currentChat.value?.custom_attributes?.omnup_ai_talimat || ''
);

// Talimat penceresi. Atama tek tiklamayla degil pencereden
// yapiliyor: temsilci atarken "bu konusmada sunu yapma" demek
// istiyor ve bunu sonradan aramak zorunda kalmamali.
const talimatPenceresi = ref(false);
const talimatIlkAtama = ref(true);

const talimatPenceresiniAc = ilk => {
  talimatIlkAtama.value = ilk;
  talimatPenceresi.value = true;
};

const talimatTamam = () => {
  talimatPenceresi.value = false;
  store.dispatch('getConversation', currentChat.value.id);
};

// Atama ve geri alma. Sunucu, atama aninda son gelen mesaja yaniti
// da kuyruga aliyor; yoksa kullanici atar ve hicbir sey olmaz.
const aiAtamasiniDegistir = async atandi => {
  try {
    await axios.post(
      `/api/v1/accounts/${hesapKimligi.value}/conversations/${currentChat.value.id}/ai-asistan`,
      { atandi }
    );
    store.dispatch('getConversation', currentChat.value.id);
    useAlert(
      atandi ? t('CONVERSATION.OMNUP_AI.ATANDI') : t('CONVERSATION.OMNUP_AI.KALDIRILDI')
    );
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        t('CONVERSATION.OMNUP_AI.HATA')
    );
  }
};

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'omnup_ai_ata') {
    talimatPenceresiniAc(true);
    return;
  }
  if (action === 'omnup_ai_talimat') {
    talimatPenceresiniAc(false);
    return;
  }
  if (action === 'omnup_ai_kaldir') {
    aiAtamasiniDegistir(false);
    return;
  }

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <OmnupAiAtamaPenceresi
      v-if="talimatPenceresi"
      :show="talimatPenceresi"
      :account-id="hesapKimligi"
      :conversation-id="currentChat.id"
      :mevcut-talimat="aiTalimat"
      :ilk-atama="talimatIlkAtama"
      @kapat="talimatPenceresi = false"
      @tamam="talimatTamam"
    />
  </div>
</template>
